import type { GeoLocation, GeoSearchResultItem, Locale } from '@smg-real-estate/geo-libs';
import { ref } from 'vue';
import {
  isNavigationFailure,
  NavigationFailureType,
  type RouteLocationRaw,
  useRoute,
  useRouter,
} from 'vue-router';

import { useLocale } from '@/router/routeMeta';
import { useGeoStore } from '@/stores/geo';

export interface DirectoryLocationSearchStore {
  changeSelectedLocations: (locations: GeoLocation[]) => void;
  nextPageRoute: (
    page: number,
    locale: Locale,
  ) => { params: { location: string | undefined }; query: Record<string, unknown> };
}

const DEFAULT_STRIP_KEYS = ['page', 'loc'];

export function useLocationSearch(
  store: DirectoryLocationSearchStore,
  options: { stripQueryKeys?: string[] } = {},
) {
  const geoStore = useGeoStore();
  const route = useRoute();
  const router = useRouter();
  const locale = useLocale();
  const geoSearchResultList = ref<GeoSearchResultItem[]>([]);
  const stripKeys = options.stripQueryKeys ?? DEFAULT_STRIP_KEYS;

  const handleSearchTextChange = async (searchText: string) => {
    if (searchText.length < 2) {
      return;
    }

    await geoStore.autocompleteLocations({ query: searchText, language: locale.value });

    const locations = geoStore.getAutocompleteResults;
    if (!locations) {
      return;
    }

    geoSearchResultList.value = locations.filter((item) => item.geoLocation.type != 'ziparea');
  };

  const applySearch = () => {
    const nextRoute = store.nextPageRoute(1, locale.value as unknown as Locale);
    const preservedQuery = Object.fromEntries(
      Object.entries(route.query).filter(([key]) => !stripKeys.includes(key)),
    );

    router
      .push({
        params: nextRoute.params,
        query: { ...preservedQuery, ...nextRoute.query },
      } as unknown as RouteLocationRaw)
      .catch((error) => {
        // Ignore duplicated navigation errors
        if (!isNavigationFailure(error, NavigationFailureType.duplicated)) {
          throw new Error(error);
        }
      });
  };

  const handleLocationChange = (locations: GeoLocation[]) => {
    store.changeSelectedLocations(locations);
    applySearch();
  };

  return { geoSearchResultList, handleSearchTextChange, handleLocationChange, applySearch };
}
